import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/site/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TYPE_OPTIONS, GRADE_OPTIONS } from "@/lib/taxonomy";
import CurriculumPicker from "@/components/admin/CurriculumPicker";
import { promptLogin } from "@/lib/authRedirect";
import { CheckCircle2, X } from "lucide-react";

// DKI Jakarta sub-regions first, then other Jabodetabek cities.
const DKI_SUBS = [
  "Jakarta Pusat", "Jakarta Utara", "Jakarta Timur", "Jakarta Selatan", "Jakarta Barat",
];
const OTHER_JABODETABEK = [
  "Depok", "Tangerang", "Tangerang Selatan", "Bekasi", "Bogor",
];

// Iter148 — form draft persists across the Google OAuth round-trip.
// `pending: true` means user tapped submit while unauth'd; we auto-resume once
// they return with a session.
const DRAFT_KEY = "k12_submit_school_draft";

const EMPTY_FORM = {
  name: "", city: "", state: "DKI Jakarta", school_type: "negeri",
  grade_levels: [], curriculum: [], description: "", website: "", image_url: "",
  brand_id: "", branch_name: "",
  entry_fee_amount: "", monthly_spp_amount: "",
  accreditation_grade: "",
};

export default function SubmitSchool() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [extraCities, setExtraCities] = useState([]);
  const [f, setF] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  // Confirmation screen state — set after successful submit so we can show
  // the school name in the copy.
  const [submittedName, setSubmittedName] = useState(null);

  // Restore-once ref so the OAuth-round-trip auto-submit doesn't loop.
  const didAutoResumeRef = useRef(false);

  useEffect(() => {
    api.get("/schools/meta/cities").then((r) => {
      const known = new Set([...DKI_SUBS, ...OTHER_JABODETABEK]);
      const extras = (r.data || []).filter((c) => !known.has(c));
      setExtraCities(extras);
    }).catch(() => {});
    // Restore draft (from a previous browsing session or from the pre-OAuth save).
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && d.form) setF({ ...EMPTY_FORM, ...d.form });
    } catch { /* corrupt draft — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGrade = (g) => {
    setF((prev) => ({
      ...prev,
      grade_levels: prev.grade_levels.includes(g)
        ? prev.grade_levels.filter((x) => x !== g)
        : [...prev.grade_levels, g],
    }));
  };

  // Auto-fill province from city selection to reduce user friction
  const onSelectCity = (v) => {
    let state = f.state;
    if (DKI_SUBS.includes(v)) state = "DKI Jakarta";
    else if (v === "Depok" || v === "Bogor" || v === "Bekasi") state = "Jawa Barat";
    else if (v === "Tangerang" || v === "Tangerang Selatan") state = "Banten";
    setF((prev) => ({ ...prev, city: v, state }));
  };

  const buildPayload = (form) => {
    // Iter151 — always emit strings (never null/undefined) for optional text
    // fields, otherwise FastAPI 422's. Numbers stay nullable (Optional[int]
    // accepts None). Arrays are flattened to strings just in case.
    const str = (v) => (v == null ? "" : String(v));
    const nn = (v) => (v === "" || v == null ? null : Number(v));
    return {
      name: str(form.name).trim(),
      city: str(form.city),
      state: str(form.state),
      school_type: str(form.school_type) || "negeri",
      grade_levels: (form.grade_levels || []).map((g) => (typeof g === "object" ? (g.value || g.label || "") : String(g))).filter(Boolean),
      curriculum: (form.curriculum || []).map((c) => (typeof c === "object" ? (c.value || c.label || "") : String(c))).filter(Boolean),
      description: str(form.description),
      website: str(form.website),
      image_url: str(form.image_url),
      brand_id: str(form.brand_id).trim(),
      branch_name: str(form.branch_name).trim(),
      entry_fee_amount: nn(form.entry_fee_amount),
      monthly_spp_amount: nn(form.monthly_spp_amount),
      accreditation_grade: str(form.accreditation_grade) || null,
    };
  };

  // Extract a human-readable error string from any axios error shape.
  // FastAPI returns 422 with `detail` as an array of {type, loc, msg, input, url}
  // objects — we join `msg` fields; strings pass through untouched.
  const extractErrorMessage = (err) => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || e?.message || "Data tidak valid").filter(Boolean).join(" · ") || "Gagal";
    if (d && typeof d === "object") return d.msg || d.message || "Gagal";
    return err?.message || "Gagal";
  };

  // Iter151 — anonymous flag lets the confirmation screen swap its notify copy.
  const [submittedAnonymously, setSubmittedAnonymously] = useState(false);

  const doSubmit = async (form, { anonymous = false } = {}) => {
    if (!form.city) { toast.error("Pilih wilayah dulu"); return; }
    setBusy(true);
    try {
      await api.post("/schools/suggestions", buildPayload(form));
      // Successful submit → wipe draft and show confirmation screen.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      setSubmittedAnonymously(anonymous);
      setSubmittedName(form.name || "sekolah tersebut");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setBusy(false); }
  };

  const submitAnonymously = () => {
    setShowLoginPrompt(false);
    // Anonymous submit: user_id/email stay null on backend since we don't send a session.
    doSubmit(f, { anonymous: true });
  };

  // Auth-round-trip resume: if we return to this page with a saved draft that
  // was marked `pending`, auto-fire submit once auth is present.
  useEffect(() => {
    if (didAutoResumeRef.current || !user) return;
    let draft;
    try { draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { draft = null; }
    if (draft && draft.pending && draft.form) {
      didAutoResumeRef.current = true;
      // Clear the pending flag first so a submit failure doesn't loop.
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: draft.form })); } catch { /* noop */ }
      doSubmit(draft.form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Nama sekolah wajib diisi"); return; }
    if (!f.city) { toast.error("Pilih wilayah dulu"); return; }
    if (!user) {
      // Guest → persist form + pending flag → open modal.
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: f, pending: true })); } catch { /* noop */ }
      setShowLoginPrompt(true);
      return;
    }
    await doSubmit(f);
  };

  // ─── Confirmation screen (post-submit) ───────────────────────────────
  if (submittedName) {
    return (
      <div data-testid="submit-school-confirmation" className="max-w-xl mx-auto px-6 py-16 text-center">
        {/* noindex applies in EVERY render state — form, login prompt, and
            this confirmation screen. Defaults (generic title/description/
            canonical) match what the App-level fallback used to emit here,
            so nothing user-visible changes; only the robots tag is added. */}
        <SEO robots="noindex,follow" />
        <div className="mx-auto w-14 h-14 rounded-full bg-[#DCFCE7] flex items-center justify-center mb-4">
          <CheckCircle2 size={30} className="text-[#16A34A]" />
        </div>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-[#1F2937] leading-tight">
          Terima kasih. Usulan Anda sudah kami terima.
        </h1>
        <p className="mt-4 text-[#4B5563] leading-relaxed">
          Tim editorial akan memeriksa informasi sebelum sekolah ditambahkan ke direktori.{" "}
          {submittedAnonymously ? (
            <span data-testid="confirm-anon-note">Kami tidak dapat mengabari Anda karena usulan dikirim tanpa akun.</span>
          ) : (
            <>Kami akan memberi tahu Anda ketika <span className="font-semibold text-[#1F2937]">{submittedName}</span> sudah tersedia.</>
          )}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            onClick={() => nav("/sekolah")}
            data-testid="confirm-see-directory"
            className="rounded-full bg-[#1B2B4D] hover:bg-[#152441] text-white h-11 px-6"
          >
            Lihat Direktori Sekolah
          </Button>
          <Button
            onClick={() => { setSubmittedName(null); setSubmittedAnonymously(false); setF(EMPTY_FORM); }}
            variant="outline"
            data-testid="confirm-submit-another"
            className="rounded-full h-11 px-6 border-[#EAE7E0] text-[#1B2B4D] hover:bg-[#F3F1ED]"
          >
            Usulkan sekolah lain
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-school-page" className="max-w-2xl mx-auto px-6 lg:px-10 py-12">
      {/* Route-level SEO ownership for /sekolah/usulkan (Sep 2026): emits
          exactly one <meta name="robots" content="noindex,follow"> via the
          shared <SEO/> mechanism. No data/auth/form-state dependency — this
          renders on the very first commit. */}
      <SEO robots="noindex,follow" />
      <div className="text-xs uppercase tracking-widest text-[#4B5563]">Kontribusi Jabodetabek</div>
      <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1F2937] mt-2">Usulkan sekolah</h1>
      <p className="mt-3 text-[#4B5563]">Editor akan meninjau usulan Anda sebelum tampil di direktori.</p>

      <form onSubmit={submit} className="mt-8 space-y-4 bg-white border border-[#EAE7E0] rounded-2xl p-6">
        <Input placeholder="Nama sekolah" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="submit-name" className="rounded-xl border-[#EAE7E0]" required />

        <div className="grid sm:grid-cols-2 gap-3">
          <Select value={f.city} onValueChange={onSelectCity}>
            <SelectTrigger data-testid="submit-city" className="rounded-xl border-[#EAE7E0]">
              <SelectValue placeholder="Wilayah" />
            </SelectTrigger>
            <SelectContent>
              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#4B5563]">DKI Jakarta</div>
              {DKI_SUBS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[#4B5563]">Sekitar Jakarta</div>
              {OTHER_JABODETABEK.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              {extraCities.length > 0 && <>
                <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[#4B5563]">Wilayah lain</div>
                {extraCities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </>}
            </SelectContent>
          </Select>
          <Input placeholder="Provinsi" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="submit-state" className="rounded-xl border-[#EAE7E0]" required />
        </div>

        <Select value={f.school_type} onValueChange={(v) => setF({ ...f, school_type: v })}>
          <SelectTrigger data-testid="submit-type" className="rounded-xl border-[#EAE7E0]"><SelectValue placeholder="Jenis sekolah" /></SelectTrigger>
          <SelectContent>
            {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">Jenjang</div>
          <div className="flex flex-wrap gap-3">
            {GRADE_OPTIONS.map((g) => (
              <div key={g.value} className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => toggleGrade(g.value)}
                  data-testid={`submit-grade-${g.value}`}
                  title={g.helper || undefined}
                  className={
                    "px-4 py-1.5 rounded-full text-sm border transition-colors duration-200 " +
                    (f.grade_levels.includes(g.value)
                      ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                      : "bg-white text-[#4B5563] border-[#EAE7E0]")
                  }
                >
                  {g.label}
                </button>
                {g.helper && (
                  <span
                    className="mt-1 text-[10px] text-[#8A8F99] leading-none"
                    data-testid={`submit-grade-${g.value}-helper`}
                  >
                    {g.helper}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="rounded-xl border border-[#EAE7E0] bg-white p-3">
            <CurriculumPicker
              value={f.curriculum}
              onChange={(next) => setF({ ...f, curriculum: next })}
              testidPrefix="submit-curriculum"
            />
          </div>
        </div>

        {/* Brand & cabang (opsional) */}
        <div className="rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-4 space-y-2" data-testid="submit-brand-block">
          <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold">Grup / Brand Sekolah (opsional)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="Nama Grup/Brand (mis: Labschool)" value={f.brand_id} onChange={(e) => setF({ ...f, brand_id: e.target.value })} data-testid="submit-brand-id" className="rounded-xl border-[#DBEAFE]" />
            <Input placeholder="Nama cabang (mis: Kebayoran)" value={f.branch_name} onChange={(e) => setF({ ...f, branch_name: e.target.value })} data-testid="submit-branch-name" className="rounded-xl border-[#DBEAFE]" />
          </div>
          <div className="text-[10px] text-[#4B5563]">Isi jika sekolah ini bagian dari grup dengan cabang lain. Boleh dikosongkan untuk sekolah mandiri.</div>
        </div>

        {/* Biaya estimasi */}
        <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4 space-y-2" data-testid="submit-cost-block">
          <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-semibold">Estimasi Biaya (Rupiah, opsional)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input type="number" min="0" placeholder="Estimasi uang pangkal" value={f.entry_fee_amount} onChange={(e) => setF({ ...f, entry_fee_amount: e.target.value })} data-testid="submit-entry-fee" className="rounded-xl border-[#FDE68A]" />
            <Input type="number" min="0" placeholder="Estimasi SPP / bulan" value={f.monthly_spp_amount} onChange={(e) => setF({ ...f, monthly_spp_amount: e.target.value })} data-testid="submit-monthly-spp" className="rounded-xl border-[#FDE68A]" />
          </div>
          <div className="text-[10px] text-[#4B5563]">Tulis angka tanpa titik/koma. Editor akan memverifikasi sebelum ditayangkan.</div>
        </div>

        {/* Akreditasi */}
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">Status Akreditasi (opsional)</div>
          <Select value={f.accreditation_grade || "none"} onValueChange={(v) => setF({ ...f, accreditation_grade: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="submit-accreditation" className="rounded-xl border-[#EAE7E0]"><SelectValue placeholder="Pilih akreditasi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Belum diakreditasi / tidak tahu</SelectItem>
              <SelectItem value="A">Akreditasi A — Sangat Baik</SelectItem>
              <SelectItem value="B">Akreditasi B — Baik</SelectItem>
              <SelectItem value="C">Akreditasi C — Cukup</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Textarea placeholder="Deskripsi singkat yang berguna untuk orang tua…" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="submit-description" className="rounded-xl border-[#EAE7E0]" />
        <Input placeholder="Situs web (opsional)" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="submit-website" className="rounded-xl border-[#EAE7E0]" />
        <Input placeholder="URL gambar sampul (opsional)" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="submit-image" className="rounded-xl border-[#EAE7E0]" />
        <Button type="submit" disabled={busy} data-testid="submit-school-submit" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white h-11 px-6">
          {busy ? "Mengirim…" : "Kirim Usulan"}
        </Button>
      </form>

      {/* ─── Login prompt modal (guest path) ─────────────────────────── */}
      {showLoginPrompt && (
        <div
          data-testid="submit-login-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setShowLoginPrompt(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowLoginPrompt(false)}
              data-testid="submit-login-modal-close"
              className="absolute top-3 right-3 text-[#4B5563] hover:text-[#1F2937] p-1 rounded-full hover:bg-[#F3F1ED]"
              aria-label="Tutup"
            >
              <X size={18} />
            </button>
            <h2 className="font-serif-display text-2xl text-[#1F2937] leading-tight">
              Simpan usulan Anda
            </h2>
            <p className="mt-3 text-[15px] text-[#4B5563] leading-relaxed">
              Masuk atau buat akun agar kami dapat mengabari Anda ketika sekolah ini sudah tersedia di The School Reports.
            </p>
            <Button
              onClick={() => promptLogin(nav)}
              data-testid="submit-login-modal-cta"
              className="mt-6 w-full rounded-full bg-[#1B2B4D] hover:bg-[#152441] text-white h-11"
            >
              Masuk / Daftar
            </Button>
            <div className="mt-3 text-center text-[11px] text-[#8A8F99]" data-testid="submit-login-modal-note">
              Usulan yang sudah Anda isi tidak akan hilang.
            </div>
            <button
              type="button"
              onClick={submitAnonymously}
              data-testid="submit-anon-link"
              className="mt-4 w-full text-center text-[12px] text-[#8A8F99] hover:text-[#4B5563] underline underline-offset-2"
            >
              Kirim tanpa akun
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
